import { useEffect, useState } from "react";
import { ApiError, confirmRegistration } from "./api/client";

/**
 * Confirming the address a certification body registered with — M9-0 phase 6b.
 *
 * **It proves an address and does nothing else.** Following this link does not sign anybody
 * in and does not set a password: the account cannot sign in until a Complao administrator
 * activates it, and that is a separate decision taken later. What it does is put the
 * registration on the administrator's register, where it is read as a claim the body has
 * made — which is why somebody has to stand behind it first.
 *
 * It confirms on load rather than behind a button. The click already happened, in the mail
 * client; asking for a second one adds a step and no information, and the token is spent
 * either way the moment the page is opened.
 *
 * There is no prototype for this screen and no door in `design/prototype/screens/` to port
 * one from. It is the fourth panel in the `.signin__*` block, built as `SetPassword.tsx` was.
 */
export function ConfirmRegistration({ token, onDone }: { token: string; onDone: () => void }) {
  const [outcome, setOutcome] = useState<"working" | "done" | "refused">("working");
  const [refusal, setRefusal] = useState<{ title: string; detail?: string } | null>(null);

  useEffect(() => {
    let live = true;
    confirmRegistration(token)
      .then(() => live && setOutcome("done"))
      .catch((cause: unknown) => {
        if (!live) return;
        // Specific about the link and silent about the account, as every credential route
        // is: the reader is holding a capability Complao sent to that address, so naming
        // what happened to it tells them nothing they did not already have.
        setRefusal(
          cause instanceof ApiError
            ? { title: cause.problem.title, detail: cause.problem.detail }
            : { title: "This link could not be checked.", detail: "Try again shortly." },
        );
        setOutcome("refused");
      });
    return () => {
      live = false;
    };
  }, [token]);

  return (
    <main className="signin">
      <div className="signin__panel">
        <div className="signin__brand">
          <div className="mark" aria-hidden="true">
            c
          </div>
          <div>
            <div className="signin__wm">complao</div>
            <div className="tag">AUDIT PLATFORM</div>
          </div>
        </div>

        {outcome === "working" ? (
          <p className="signin__tagline">Confirming this address…</p>
        ) : outcome === "refused" ? (
          <>
            <p className="problem" role="alert">
              <strong>{refusal?.title}</strong>
              {refusal?.detail ? <> {refusal.detail}</> : null}
            </p>
            <p className="signin__tagline">
              Registering the same address again sends a new confirmation link.
            </p>
          </>
        ) : (
          <>
            <p className="callout green" role="status">
              <b>Address confirmed.</b> Your registration is now with a Complao
              administrator.
            </p>
            <p className="signin__tagline">
              A Complao administrator reviews new certification bodies before they can sign
              in. You will be sent a link to set a password when yours is activated.
            </p>
          </>
        )}

        <div className="signin__actions">
          <button className="btn" type="button" onClick={onDone}>
            Go to sign in
          </button>
        </div>
      </div>
    </main>
  );
}
